import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SHOP_ORDER_TYPES, SHOP_ORDER_META, SHOP_PRIORITIES, truckNeedOf, truckRequestTitle, reservationTitle } from '../shopOrderTemplates';
import { useAuth } from '../context/AuthContext';
import AttachButton, { AttachThumb, isPdf } from '../components/AttachButton';
import RequestTruckFields from './RequestTruckFields';
import ReserveAssetFields from './ReserveAssetFields';
import JobSearchSelect from './JobSearchSelect';
import DeliveryLocationModal from '../components/DeliveryLocationModal';
import { jobLabel } from '../utils/ui';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://app.titanelectricreno.com:8080/api';
function getToken() { try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.token; } catch { return null; } }
function apiFetch(path, opts = {}) {
  const token = getToken();
  return fetch(`${API_BASE}${path}`, { ...opts, headers: { 'Content-Type': 'application/json', ...(token ? { 'x-worker-token': token } : {}), ...opts.headers } })
    .then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.error || `Request failed (${r.status})`); return d; });
}

export default function ShopRequest() {
  const navigate = useNavigate();
  const { worker } = useAuth() || {};
  const [params] = useSearchParams();
  const presetProject = params.get('project') || '';
  // The equipment card's "Repair Ticket" button lands here with the type and machine set.
  const presetType = SHOP_ORDER_META[params.get('type') || ''] ? params.get('type') : '';
  const presetEquipment = params.get('equipment') || '';
  const [projects, setProjects] = useState([]);
  const [projLoc, setProjLoc] = useState({}); // project_id -> { address, lat, lng }
  const [type, setType] = useState(presetType);
  const [f, setF] = useState({ title: '', project_id: presetProject, equipment_id: presetEquipment, priority: 'normal', needed_by: '', notes: '', details: {} });
  const [equipment, setEquipment] = useState([]);
  const [pending, setPending] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');
  const [showDeliveryMap, setShowDeliveryMap] = useState(false);

  useEffect(() => {
    apiFetch('/my-projects').then(d => setProjects(Array.isArray(d) ? d : [])).catch(() => {});
    apiFetch('/projects-map').then(pm => {
      const loc = {}; (Array.isArray(pm) ? pm : []).forEach(x => { loc[x.id] = { address: x.address || '', lat: x.lat, lng: x.lng }; });
      setProjLoc(loc);
    }).catch(() => {});
    // Default the job to whatever the worker is clocked into (unless a project
    // was passed in via the URL).
    if (!presetProject) apiFetch('/time/status').then(s => { if (s && s.projectId) setF(v => v.project_id ? v : { ...v, project_id: s.projectId }); }).catch(() => {});
    apiFetch('/equipment').then(e => setEquipment(Array.isArray(e) ? e : [])).catch(() => {});
  }, []);

  const meta = type ? SHOP_ORDER_META[type] : null;
  function set(k, v) { setF(s => ({ ...s, [k]: v })); }
  function setDetail(k, v) { setF(s => ({ ...s, details: { ...s.details, [k]: v } })); }

  // Prefill Deliver To from the job's saved map location (still editable). Never
  // touches the job's own address — this only fills the shop order's field.
  useEffect(() => {
    if (!meta || !Array.isArray(meta.fields) || !meta.fields.some(fl => fl.key === 'deliver_to')) return;
    if (f.details.deliver_to) return;
    const addr = f.project_id && projLoc[f.project_id] && projLoc[f.project_id].address;
    if (addr) setDetail('deliver_to', addr);
  }, [type, f.project_id, projLoc]); // eslint-disable-line react-hooks/exhaustive-deps

  // Title: the two structured requests write their own ("Move T-12 → 2631", "Alex · Big back
  // trailer · Thu Oct 2 · 9:00 AM") — nobody has to think one up (Ryan, Oct 1 2026).
  const autoTitle = type === 'request_truck' ? (truckNeedOf(f.details) ? truckRequestTitle(f.details) : '')
    : type === 'request_equipment' ? ((f.details.asset_id || f.details.custom_asset) ? reservationTitle(f.details, f.needed_by) : '') : '';
  async function submit() {
    if (type === 'request_truck') {
      const need = truckNeedOf(f.details);
      if (!need) { setErr('Pick what you need'); return; }
      if (need === 'move_equipment' && !f.details.equipment_id && !(f.details.custom_equipment || '').trim()) { setErr('Pick what is moving'); return; }
      if (need === 'other' && !(f.details.what || '').trim()) { setErr('Describe what you need'); return; }
      if (!f.needed_by) { setErr('Pick the day you need it'); return; }
    } else if (type === 'request_equipment') {
      if (!f.details.asset_id && !(f.details.custom_asset || '').trim()) { setErr('Pick the asset'); return; }
      if (!f.details.for_user_id) { setErr('Pick who needs it'); return; }
      if (!f.needed_by) { setErr('Pick the day'); return; }
    } else if (!f.title.trim()) { setErr('Add a short title'); return; }
    setSubmitting(true); setErr('');
    try {
      const saved = await apiFetch('/shop-orders', { method: 'POST', body: JSON.stringify({ type, title: f.title.trim() || autoTitle, project_id: f.project_id || null, equipment_id: f.equipment_id || null, priority: f.priority, needed_by: f.needed_by || null, notes: f.notes, details: f.details }) });
      if (!saved || !saved.id) throw new Error('Save failed');
      for (const file of pending) { const fd = new FormData(); fd.append('file', file); await fetch(`${API_BASE}/shop-orders/${saved.id}/photos`, { method: 'POST', headers: { 'x-worker-token': getToken() }, body: fd }); }
      setDone(true);
    } catch (e) { setErr('Could not submit — try again'); } finally { setSubmitting(false); }
  }

  const inp = { border: '1px solid var(--border, #e5e7eb)', borderRadius: 10, padding: '12px', fontSize: 16, width: '100%', boxSizing: 'border-box', background: 'var(--card, #fff)', color: 'var(--text)' };
  const lbl = { fontSize: 12, fontWeight: 700, color: 'var(--gray-muted, #6b7280)', textTransform: 'uppercase', marginBottom: 6, display: 'block' };

  if (done) {
    return (
      <div className="page">
        <div className="top-bar"><div className="top-bar-wordmark">TITAN <span>ELECTRIC</span></div></div>
        <div style={{ textAlign: 'center', padding: '60px 24px' }}>
          <div style={{ fontSize: 54, marginBottom: 12 }}>✅</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text)', marginBottom: 6 }}>Request sent to the shop</div>
          <div style={{ fontSize: 14, color: 'var(--gray-muted)', marginBottom: 28 }}>The shop and office have been notified.</div>
          <button onClick={() => navigate('/dashboard')} style={{ width: '100%', maxWidth: 320, padding: 14, background: '#f97316', color: '#fff', border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: 'pointer' }}>Done</button>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="top-bar">
        <button onClick={() => (type ? setType('') : navigate(-1))} style={{ background: 'none', border: 'none', color: '#fff', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M11 4L6 9l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
          <span style={{ fontSize: 14, fontWeight: 600 }}>Back</span>
        </button>
      </div>

      <div style={{ padding: '18px 16px 100px' }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text)', marginBottom: 14 }}>📦 Request from Shop</div>

        {!type ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
            {SHOP_ORDER_TYPES.map(t => (
              <button key={t.key} onClick={() => setType(t.key)} style={{ textAlign: 'left', background: 'var(--card, #fff)', border: '1px solid var(--border, #e5e7eb)', borderLeft: `5px solid ${t.color}`, borderRadius: 12, padding: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 14 }}>
                <span style={{ fontSize: 28 }}>{t.icon}</span>
                <span>
                  <span style={{ display: 'block', fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{t.label}</span>
                  <span style={{ display: 'block', fontSize: 13, color: 'var(--gray-muted)' }}>{t.blurb}</span>
                </span>
              </button>
            ))}
          </div>
        ) : (
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: meta.color, marginBottom: 14 }}>{meta.icon} {meta.label}</div>

            {/* Fix Something Broken: which truck or machine, first — the ticket lands on that
                machine's card (Repair Tickets) so the shop sees it against the right iron. */}
            {type === 'fix_broken' && (() => {
              const picked = equipment.find(e => e.id === f.equipment_id);
              return (
                <div style={{ marginBottom: 14, padding: '12px 12px 10px', borderRadius: 12, border: `1.5px solid ${picked ? '#fecaca' : 'var(--border, #e5e7eb)'}`, background: picked ? '#fff7f7' : 'var(--card, #fff)' }}>
                  <label style={lbl}>🔧 Piece of equipment (truck or machine)</label>
                  <select value={f.equipment_id} onChange={e => set('equipment_id', e.target.value)} style={inp}>
                    <option value="">— Not a truck or machine (a tool, the building…) —</option>
                    {[...equipment]
                      .sort((a, b) => String(a.asset_number || a.name).localeCompare(String(b.asset_number || b.name), undefined, { numeric: true }))
                      .map(eq => (
                        <option key={eq.id} value={eq.id}>
                          {[eq.asset_number, [eq.year, eq.make, eq.model].filter(Boolean).join(' ') || eq.name].filter(Boolean).join(' · ')}
                        </option>
                      ))}
                  </select>
                  <div style={{ fontSize: 12, color: picked ? '#9a3412' : 'var(--gray-muted, #6b7280)', marginTop: 6 }}>
                    {picked
                      ? `${picked.category ? picked.category + ' · ' : ''}📍 ${picked.current_location || 'Shop'}${picked.assigned_to ? ' · 👤 ' + picked.assigned_to : ''} — this ticket goes on its card.`
                      : 'Pick the truck or machine so the repair shows up on its card and history.'}
                  </div>
                </div>
              );
            })()}

            {!meta.custom && <div style={{ marginBottom: 12 }}><label style={lbl}>Title</label><input value={f.title} onChange={e => set('title', e.target.value)} style={inp} placeholder="Short summary" /></div>}
            {meta.custom ? (
              type === 'request_equipment'
                ? <ReserveAssetFields details={f.details} setDetail={setDetail} projectId={f.project_id} onProject={v => set('project_id', v)} mobile
                    neededBy={f.needed_by} onNeededBy={v => set('needed_by', v)} onEquipment={v => set('equipment_id', v)} selfId={worker?.id} />
                : <RequestTruckFields details={f.details} setDetail={setDetail} projectId={f.project_id} onProject={v => set('project_id', v)} mobile
                    neededBy={f.needed_by} onNeededBy={v => set('needed_by', v)} onEquipment={v => set('equipment_id', v)} />
            ) : meta.fields.map(fl => (
              <div key={fl.key} style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                  <label style={lbl}>{fl.label}</label>
                  {fl.key === 'deliver_to' && (
                    <div style={{ display: 'flex', gap: 14 }}>
                      {f.project_id && projLoc[f.project_id]?.address && (
                        <button type="button" onClick={() => setDetail('deliver_to', projLoc[f.project_id].address)}
                          style={{ background: 'none', border: 'none', color: '#1d4ed8', fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: 0 }}>📍 Use job location</button>
                      )}
                      <button type="button" onClick={() => setShowDeliveryMap(true)}
                        style={{ background: 'none', border: 'none', color: '#e87722', fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: 0 }}>🗺 Pin drop</button>
                    </div>
                  )}
                </div>
                {fl.type === 'textarea' ? <textarea value={f.details[fl.key] || ''} onChange={e => setDetail(fl.key, e.target.value)} rows={2} style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }} />
                  : fl.type === 'select' ? <select value={f.details[fl.key] || ''} onChange={e => setDetail(fl.key, e.target.value)} style={inp}><option value="">—</option>{fl.options.map(o => <option key={o} value={o}>{o}</option>)}</select>
                  : <input type={fl.type === 'date' ? 'date' : 'text'} value={f.details[fl.key] || ''} onChange={e => setDetail(fl.key, e.target.value)} style={inp} />}
                {fl.key === 'deliver_to' && f.details.deliver_lat != null && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, fontSize: 12, color: '#9a3412' }}>
                    <span>📍 Exact drop point pinned</span>
                    <button type="button" onClick={() => { setDetail('deliver_lat', undefined); setDetail('deliver_lng', undefined); }} style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: 0 }}>clear</button>
                  </div>
                )}
                {fl.key === 'deliver_to' && (f.details.deliver_lat != null || f.details.deliver_to) && (
                  <a href={f.details.deliver_lat != null
                    ? `https://www.google.com/maps/dir/?api=1&destination=${f.details.deliver_lat},${f.details.deliver_lng}`
                    : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(f.details.deliver_to)}`} target="_blank" rel="noreferrer"
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 8, padding: '12px', borderRadius: 10, border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', fontSize: 15, fontWeight: 700, textDecoration: 'none' }}>
                    🗺 Open map &amp; directions
                  </a>
                )}
              </div>
            ))}

            {!presetProject && !meta.custom && (
              <div style={{ marginBottom: 12 }}><label style={lbl}>Job (optional)</label>
                <JobSearchSelect value={f.project_id} projects={projects} onChange={v => set('project_id', v)} />
              </div>
            )}
            <div style={{ marginBottom: 12 }}><label style={lbl}>Priority</label><select value={f.priority} onChange={e => set('priority', e.target.value)} style={inp}>{SHOP_PRIORITIES.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}</select></div>
            {!meta.custom && <div style={{ marginBottom: 12 }}><label style={lbl}>Needed by</label><input type="date" value={f.needed_by} onChange={e => set('needed_by', e.target.value)} style={inp} /></div>}
            <div style={{ marginBottom: 12 }}><label style={lbl}>Notes</label><textarea value={f.notes} onChange={e => set('notes', e.target.value)} rows={2} style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }} /></div>

            <div style={{ marginBottom: 14 }}>
              <label style={lbl}>Photos &amp; files</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                {pending.map((file, i) => (
                  <AttachThumb key={i} size={80} src={isPdf(file) ? null : URL.createObjectURL(file)} name={file.name}
                    onRemove={() => setPending(p => p.filter((_, j) => j !== i))} />
                ))}
              </div>
              <AttachButton multiple icon="📷" onFiles={fs => setPending(p => [...p, ...fs])} />
            </div>

            {meta.custom && autoTitle && <div style={{ marginBottom: 12, padding: '10px 12px', borderRadius: 10, background: 'var(--card, #fff)', border: '1px solid var(--border, #e5e7eb)', fontSize: 14, color: 'var(--text)' }}><span style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-muted, #6b7280)', textTransform: 'uppercase', display: 'block', marginBottom: 2 }}>The shop will see</span><b>{autoTitle}</b></div>}
            {err && <div style={{ color: '#dc2626', fontSize: 14, marginBottom: 12 }}>{err}</div>}
            <button onClick={submit} disabled={submitting} style={{ width: '100%', padding: 16, background: submitting ? '#c9b8ad' : '#f97316', color: '#fff', border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 800, cursor: 'pointer' }}>{submitting ? 'Sending…' : 'Send Request'}</button>
          </div>
        )}
      </div>

      {showDeliveryMap && (
        <DeliveryLocationModal
          projectId={f.project_id}
          initial={{ lat: f.details.deliver_lat, lng: f.details.deliver_lng }}
          onClose={() => setShowDeliveryMap(false)}
          onSave={({ lat, lng, address }) => {
            setF(s => ({ ...s, details: { ...s.details, deliver_lat: lat, deliver_lng: lng, ...(address ? { deliver_to: address } : {}) } }));
          }}
        />
      )}
    </div>
  );
}
